import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNominationWizardContext } from '../../../hooks/useCaseRequestWizard';
import type { NominationWizardDraft } from '../../../types/nominationCaseRequest';
import { useWizardMaxLengthChange } from '../useWizardMaxLengthChange';

import { focusFirstInvalid, type RequiredErrors } from '../../../utils/requiredValidation';
import { SectionCard } from '../WizardComponents';
import { NominationWizardActions } from './NominationWizardActions';
import { nominationWizardPath, NominationWizardShell, FieldError, inpClass } from './NominationWizardShell';
import { validateNominationStep } from './nominationStepValidation';
import { useWizardFieldLabels } from '../useWizardFieldLabels';

const MAX_DESC = 2000;
const MAX_REF = 255;

export default function NominationStep1CaseOverviewPage() {
  const f = useWizardFieldLabels();
  const nav = useNavigate();
  const { draft, setDraft } = useNominationWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});

  const clearErr = (key: string) => setErrors((e) => ({ ...e, [key]: '' }));
  const maxLen = useWizardMaxLengthChange(setErrors);

  useEffect(() => {
    if (draft.hasConnectedMatters === 'yes' || draft.hasConnectedMatters === 'no') return;
    setDraft((d: NominationWizardDraft) => ({ ...d, hasConnectedMatters: 'no' }));
  }, [draft.hasConnectedMatters, setDraft]);

  const validate = (): boolean => {
    const { valid, errors: nextErrors, specs } = validateNominationStep(1, draft);
    setErrors(nextErrors);
    if (!valid) focusFirstInvalid(specs, nextErrors);
    return valid;
  };

  const next = () => {
    if (!validate()) return;
    nav(nominationWizardPath(2, draft.draftRequestId));
  };

  return (
    <NominationWizardShell
      currentStep={1}
      draft={draft}
      setDraft={setDraft}
      categorizationEditable
      categorizationErrors={errors}
      onClearCategorizationError={clearErr}
    >
      <SectionCard title={f.sectionCaseIntakeDetails}>
        <div className="ccr-fg" style={{ marginBottom: 14 }}>
          <label>
            {f.description} <span className="req">*</span>
          </label>
          <textarea
            id="nom-description"
            className={inpClass(Boolean(errors.description))}
            rows={4}            value={draft.description}
            onChange={(e) => {
              setDraft((d: NominationWizardDraft) => ({ ...d, description: maxLen('description', e.target.value, MAX_DESC) }));
            }}
            placeholder={f.briefDescriptionOfCase}
          />
          <FieldError message={errors.description} />
        </div>

        <div className="ccr-fg" style={{ marginBottom: 14 }}>
          <label>{f.caseNumber}</label>
          <input
            className={inpClass(Boolean(errors.caseNumber))}
            type="text"            value={draft.caseNumber}
            onChange={(e) =>
              setDraft((d: NominationWizardDraft) => ({ ...d, caseNumber: maxLen('caseNumber', e.target.value, MAX_REF) }))
            }
            placeholder={f.courtReferenceNumber}
          />
          <FieldError message={errors.caseNumber} />
        </div>

        <div
          className="ccr-fg"
          id="nom-connectedMatters"
          style={{ marginBottom: draft.hasConnectedMatters === 'yes' ? 14 : 0 }}
        >
          <label>{f.connectedMatters}</label>
          <div className="ccr-radio-row">
            <label className="ccr-radio">
              <input
                type="radio"
                name="connectedMatters"
                checked={draft.hasConnectedMatters === 'yes'}
                onChange={() => {
                  setDraft((d: NominationWizardDraft) => ({ ...d, hasConnectedMatters: 'yes' }));
                }}
              />
              <span>{f.yes}</span>
            </label>
            <label className="ccr-radio">
              <input
                type="radio"
                name="connectedMatters"
                checked={draft.hasConnectedMatters === 'no'}
                onChange={() => {
                  setDraft((d: NominationWizardDraft) => ({
                    ...d,
                    hasConnectedMatters: 'no',
                    connectedCaseNumber: '',
                    connectedAgdFileRef: '' }));
                }}
              />
              <span>{f.no}</span>
            </label>
          </div>
        </div>

        {draft.hasConnectedMatters === 'yes' ? (
          <div className="ccr-fr c2" style={{ marginBottom: 0 }}>
            <div className="ccr-fg">
              <label>{f.connectedCaseNumber} <span className="req">*</span></label>
              <input
                id="nom-connectedCaseNo"
                className={inpClass(Boolean(errors.connectedCaseNumber))}
                type="text"                value={draft.connectedCaseNumber}
                onChange={(e) => {
                  setDraft((d: NominationWizardDraft) => ({
                    ...d,
                    connectedCaseNumber: maxLen('connectedCaseNumber', e.target.value, MAX_REF) }));
                }}
                placeholder={f.enterConnectedCaseNumber}
              />
              <FieldError message={errors.connectedCaseNumber} />
            </div>
            <div className="ccr-fg">
              <label>{f.connectedAgdFileRef}</label>
              <input
                id="nom-connectedAgd"
                className={inpClass(Boolean(errors.connectedAgdFileRef))}
                type="text"                value={draft.connectedAgdFileRef}
                onChange={(e) => {
                  setDraft((d: NominationWizardDraft) => ({
                    ...d,
                    connectedAgdFileRef: maxLen('connectedAgdFileRef', e.target.value, MAX_REF) }));
                }}
                placeholder={f.enterConnectedAgdFileRef}
              />
              <FieldError message={errors.connectedAgdFileRef} />
            </div>
          </div>
        ) : null}
      </SectionCard>

      <NominationWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={1}
        onNext={next}
        validateBeforeSave={validate}
      />
    </NominationWizardShell>
  );
}
